import React, { useEffect, useRef, useState } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { Button } from "antd";
import errorImg from "../../assets/images/no-img.png";

const AZURE_KEY = process.env.REACT_APP_AZURE_KEY;
const AZURE_REGION = process.env.REACT_APP_AZURE_REGION;

const WordCard = ({
  word,
  img,
  meaning,
  audio,
  onClick,
  onPrev,
  onNext,
  canGoPrev,
  refs,
}) => {
  const audioRef = useRef(null);
  const [showSpelling, setShowSpelling] = useState(false);
  const { ref2, ref3 } = refs;

  const playAudio = (e) => {
    if (audio) {
      if (audioRef.current) {
        audioRef.current.play();
      }
    } else {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        AZURE_KEY,
        AZURE_REGION
      );
      speechConfig.speechRecognitionLanguage = "en-US";

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultSpeakerOutput();
      const synthesizer = new SpeechSDK.SpeechSynthesizer(
        speechConfig,
        audioConfig
      );

      synthesizer.speakTextAsync(
        word,
        (result) => {
          if (
            result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted
          ) {
            console.log("Synthesis finished.");
          } else {
            console.error("Speech synthesis canceled, " + result.errorDetails);
          }
          synthesizer.close();
        },
        (error) => {
          console.error(error);
          synthesizer.close();
        }
      );
    }
  };

  const handleNext = (e) => {
    if (!showSpelling) {
      setShowSpelling(true);
    } else {
      setShowSpelling(false);
      onNext(e);
    }
  };

  useEffect(() => {
    setShowSpelling(false);
  }, [word]);

  return (
    <div
      style={{ maxWidth: "1200px", margin: "0 auto" }}
      className="relative cursor-pointer w-full p-2"
      onClick={onClick}
    >
      <div className="w-full md:mt-[50px] flex flex-col gap-10 items-center justify-center">
        <div className="relative pb-[100px] md:pb-0 flex justify-center items-center gap-2 md:gap-[50px]">
          <Button
            className="prev-btn rounded-[50%] bg-[#3D90EF] text-[#fff]"
            style={{ width: "55px", height: "55px" }}
            icon={<LeftOutlined style={{ fontSize: "30px" }} />}
            onClick={onPrev}
            disabled={!canGoPrev}
          />

          <motion.div
            key={word}
            className="card-img"
            onClick={(e) => {
              e.stopPropagation();
              setShowSpelling(!showSpelling);
            }}
            initial={{ x: -100, y: 100, scale: 0.5, opacity: 0 }}
            animate={{ x: 0, y: 0, scale: 1.1, opacity: 1 }}
            transition={{
              duration: 0.6,
              ease: [0.25, 1, 0.5, 1],
            }}
          >
            <img
              src={img}
              alt={word}
              className="object-contain"
              onError={(e) => (e.target.src = errorImg)}
              ref={ref2}
            />
          </motion.div>

          <Button
            style={{ width: "55px", height: "55px" }}
            className="next-btn rounded-[50%] bg-[#3D90EF] text-[#fff]"
            icon={<RightOutlined style={{ fontSize: "30px" }} />}
            onClick={handleNext}
          />
        </div>

        <div
          ref={ref3}
          className="flex flex-col items-center justify-center gap-8 py-5"
        >
          <div className="relative pb-[60px] md:pb-0 w-full flex items-center justify-center gap-[20px]">
            <button onClick={playAudio} className="md:p-2 play-audio-btn">
              <svg
                className="w-[35px] h-[35px]"
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="50"
                viewBox="0 0 48 50"
                fill="none"
              >
                <path
                  d="M28.2783 1.36113e-05C27.1884 0.00251359 26.0324 0.3614 25.3099 0.781119C24.0302 1.52472 14.2206 8.33327 14.2206 8.33327C14.2206 8.33327 6.74968 8.32771 5.83824 8.33327C2.81919 8.35216 0.272074 10.9241 0.25 13.8888V36.1108C0.282971 39.2046 2.67362 41.6955 5.83824 41.6663C6.62813 41.6591 14.2206 41.6663 14.2206 41.6663C14.2206 41.6663 24.2213 48.5429 25.4842 49.3051C26.2169 49.747 27.0478 50.0126 28.1912 49.9995C31.3142 49.9637 33.816 47.3885 33.7794 44.3574V24.9998V5.55552C33.7763 2.43638 31.2982 -0.00665299 28.2783 1.36113e-05ZM42.511 13.8888C41.8027 13.791 41.1182 13.9468 40.5026 14.4096C39.2721 15.3351 38.9605 17.0924 39.8915 18.316C41.34 20.2195 42.1618 22.5337 42.1618 24.9998C42.1618 27.4392 41.3341 29.7739 39.8915 31.6836C38.965 32.9105 39.2687 34.6689 40.5026 35.59C41.7368 36.5111 43.5055 36.2091 44.432 34.9825C46.5949 32.1189 47.75 28.6534 47.75 24.9998C47.75 21.3179 46.6108 17.8812 44.432 15.0171C43.9665 14.4054 43.2193 13.9865 42.511 13.8888Z"
                  fill="#3D90EF"
                />
              </svg>
            </button>

            <div className="flex flex-wrap justify-center gap-1 max-w-full">
              {word.split("").map((letter, index) =>
                letter.match(/[ .?'-]/) ? ( // 공백인 경우
                  <div className="inline-block w-5 flex items-center justify-center">
                    <span
                      key={index}
                      className="text-[#3D90EF] roboto-bold text-[30px]"
                    >
                      {letter}
                    </span>
                  </div>
                ) : (
                  <div
                    key={index}
                    className="w-10 h-10 md:w-12 md:h-12 overflow-hidden border-2 bg-[#e6f3ff] border-[#3D90EF] rounded flex items-center justify-center"
                  >
                    <motion.span
                      className="text-[#3D90EF] roboto-bold text-[30px]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: showSpelling ? 1 : 0 }}
                      transition={{
                        duration: showSpelling ? 0.2 : 0,
                        delay: showSpelling ? index * 0.05 : 0,
                      }}
                    >
                      {letter}
                    </motion.span>
                  </div>
                )
              )}
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowSpelling(!showSpelling);
              }}
              className="spelling-btn"
            >
              {!showSpelling ? (
                <svg
                  className="w-[40px] h-[40px]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="50"
                  height="50"
                  viewBox="0 0 50 50"
                  fill="none"
                >
                  <path
                    d="M8.33274 8.33331L41.6661 41.6666M34.3744 34.908C31.5565 36.426 28.3715 37.4997 24.9994 37.4997C17.6439 37.4997 11.1793 32.3913 7.47214 28.7058C6.4936 27.733 6.0044 27.2467 5.69297 26.2919C5.47087 25.611 5.4708 24.3887 5.69297 23.7078C6.00449 22.753 6.49471 22.2655 7.4752 21.291C9.3437 19.4337 11.9122 17.2158 14.9417 15.4724M40.6244 30.4863C41.3183 29.8761 41.9537 29.2757 42.524 28.7089L42.53 28.7025C43.5064 27.7321 43.9959 27.2456 44.3067 26.2929C44.5289 25.612 44.5284 24.3892 44.3063 23.7083C43.995 22.7539 43.5052 22.2661 42.5273 21.294C38.8201 17.6085 32.3549 12.5 24.9994 12.5C24.2963 12.5 23.6013 12.5467 22.9161 12.6343M27.7555 28.125C27.021 28.7733 26.0561 29.1666 24.9994 29.1666C22.6982 29.1666 20.8327 27.3012 20.8327 25C20.8327 23.876 21.2778 22.8564 22.0012 22.1069"
                    stroke="#3D90EF"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  className="w-[40px] h-[40px]"
                  xmlns="http://www.w3.org/2000/svg"
                  width="50"
                  height="50"
                  viewBox="0 0 50 50"
                  fill="none"
                >
                  <path
                    d="M7.47213 28.7059C11.1793 32.3913 17.6445 37.4998 25 37.4998C32.3555 37.4998 38.8195 32.3913 42.5267 28.7059C43.5044 27.7339 43.9949 27.2462 44.3062 26.2919C44.5283 25.611 44.5283 24.389 44.3062 23.7081C43.9949 22.7538 43.5044 22.266 42.5267 21.294C38.8195 17.6085 32.3555 12.5 25 12.5C17.6445 12.5 11.1793 17.6085 7.47213 21.294C6.49367 22.2667 6.0044 22.7534 5.69296 23.7081C5.47084 24.389 5.47084 25.611 5.69296 26.2919C6.0044 27.2466 6.49367 27.7331 7.47213 28.7059Z"
                    stroke="#3D90EF"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.833 25C20.833 27.3012 22.6984 29.1667 24.9996 29.1667C27.3008 29.1667 29.1663 27.3012 29.1663 25C29.1663 22.6988 27.3008 20.8333 24.9996 20.8333C22.6984 20.8333 20.833 22.6988 20.833 25Z"
                    stroke="#3D90EF"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>

          <div className=" text-[32px] md:text-[40px] font-bold text-gray-800 gmarket-sans">
            {meaning}
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={audio} />
    </div>
  );
};

export default WordCard;
